import React, { useState } from 'react';
import { Bot, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Chatbot {
  id: string;
  name: string;
}

interface EnhancedChatbotInputProps {
  chatbots: string[];
  setChatbots: (chatbots: string[]) => void;
  availableChatbots: Chatbot[];
  disabled?: boolean;
  maxDisplayCount?: number;
}

export const EnhancedChatbotInput = ({
  chatbots,
  setChatbots,
  availableChatbots,
  disabled = false,
  maxDisplayCount = 3
}: EnhancedChatbotInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const displayChatbots = chatbots.slice(0, maxDisplayCount);
  const remainingCount = chatbots.length - maxDisplayCount;
  
  const availableChatbotsToShow = availableChatbots.filter(chatbot => !chatbots.includes(chatbot.id));

  const handleAddChatbot = (chatbotId: string) => {
    if (!chatbots.includes(chatbotId)) {
      setChatbots([...chatbots, chatbotId]);
    }
  };

  const handleRemoveChatbot = (chatbotIdToRemove: string) => {
    setChatbots(chatbots.filter(chatbotId => chatbotId !== chatbotIdToRemove));
  };

  const getChatbotName = (chatbotId: string) => {
    const chatbot = availableChatbots.find(c => c.id === chatbotId);
    return chatbot?.name || chatbotId;
  };

  return (
    <div className="space-y-2">
      {/* Display selected chatbots */}
      {chatbots.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {displayChatbots.map((chatbotId, index) => (
            <Badge 
              key={index} 
              variant="secondary"
              className="flex items-center gap-1 py-1 pl-2 pr-1"
            >
              <Bot className="h-3 w-3 opacity-70" />
              {getChatbotName(chatbotId)}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveChatbot(chatbotId)}
                  className="rounded-full hover:bg-muted focus:outline-none"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
          
          {remainingCount > 0 && (
            <Badge variant="outline" className="flex items-center gap-1 py-1">
              +{remainingCount} more
            </Badge>
          )}
        </div>
      )}
      
      {/* Chatbot selector */}
      <Select 
        open={isOpen} 
        onOpenChange={setIsOpen}
        onValueChange={(value) => {
          if (value) {
            handleAddChatbot(value);
            // Reset the select value so the same chatbot can be selected again if needed
            setTimeout(() => {
              const selectElement = document.querySelector('[data-radix-select-trigger]') as HTMLButtonElement;
              if (selectElement) {
                selectElement.click();
              }
            }, 100);
          }
        }}
        disabled={disabled || availableChatbotsToShow.length === 0}
      >
        <SelectTrigger className="w-full focus:ring-primary/30 focus:border-primary">
          <SelectValue placeholder={availableChatbotsToShow.length === 0 ? "No chatbots available" : "Add chatbots..."} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {availableChatbotsToShow.length > 0 && (
              availableChatbotsToShow.map((chatbot) => (
                <SelectItem key={chatbot.id} value={chatbot.id}>
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-muted-foreground" />
                    <span>{chatbot.name}</span>
                  </div>
                </SelectItem>
              ))
            )}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}; 